// Course Package Import — Step 4/5 org-scoped upload pass.
// Uploads the files that do NOT need a block to exist: thumbnail, slide decks
// and lab datasets. Reading images + reading PDFs are block-scoped and are
// uploaded by packageBlocks AFTER the RICHTEXT block is created (works for
// all roles, not just SuperAdmin). Records each upload against its block key,
// with a durable ledger so a rerun skips files already uploaded.

const MAX_FILE_BYTES = 50 * 1024 * 1024;
const ext = (p) => (String(p).split('.').pop() || '').toLowerCase();
const errText = (e) => e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || 'upload failed';
const ledgerKey = (orgId, pkg, rel) => `${orgId}::${pkg}::${rel}`;
function blob(bytes, type) { return new Blob([bytes], type ? { type } : undefined); }
// shared api client defaults to application/json; drop it for multipart.
const MULTIPART = { headers: { 'Content-Type': undefined } };

export async function uploadAssets(o) {
  const { api, orgId, readResult, plan } = o;
  const onProgress = o.onProgress || (() => {});
  const ledger = o.ledger || {};
  const checkExists = o.checkExists; // optional async (desc) => bool
  const files = readResult.files || new Map();
  const index = readResult.index || {};
  const pkg = readResult.packageName || 'package';

  const assets = { thumbnailUrl: null, blocks: {} };
  const uploads = [];
  const blockOf = (k) => (assets.blocks[k] || (assets.blocks[k] = { deck: null, datasets: [], readingHtml: null, pdf: null, images: {} }));
  const tooBig = (rel) => (files.get(rel)?.length || 0) > MAX_FILE_BYTES;
  const record = (item) => uploads.push(item);

  // ledger-aware upload; re-checks existence before reuse when checkExists given
  async function viaLedger(rel, doUpload) {
    const key = ledgerKey(orgId, pkg, rel);
    const cached = ledger[key];
    if (cached) {
      let ok = true;
      if (checkExists) { try { ok = await checkExists(cached); } catch (e) { ok = true; } }
      if (ok) return { ...cached, cached: true };
    }
    const desc = await doUpload();
    ledger[key] = desc;
    return desc;
  }
  async function uploadGeneric(rel, type) {
    const bytes = files.get(rel); const name = rel.split('/').pop();
    const fd = new FormData(); fd.append('file', blob(bytes), name); fd.append('type', type);
    const r = await api.post(`/organizations/${orgId}/upload`, fd, MULTIPART);
    return { fileId: r.data.fileId, gridfsId: r.data.gridfsId, url: r.data.url, name };
  }
  async function uploadDataset(rel) {
    const bytes = files.get(rel); const name = rel.split('/').pop();
    const fd = new FormData(); fd.append('file', blob(bytes), name); fd.append('name', name.replace(/\.[^.]+$/, ''));
    const r = await api.post(`/orgs/${orgId}/datasets/upload`, fd, MULTIPART);
    return { datasetId: r.data.id, name: r.data.name, fileType: r.data.fileType };
  }

  // thumbnail
  const thumb = plan?.course?.thumbnailPath;
  if (thumb && files.has(thumb)) {
    onProgress({ phase: 'files', label: 'thumbnail' });
    if (tooBig(thumb)) record({ key: 'thumbnail', kind: 'thumbnail', status: 'refused', message: `${thumb} is over 50 MB` });
    else try {
      const d = await viaLedger(thumb, () => uploadGeneric(thumb, 'thumbnail'));
      assets.thumbnailUrl = d.url;
      record({ key: 'thumbnail', kind: 'thumbnail', status: d.cached ? 'skipped' : 'uploaded', url: d.url, message: d.cached ? 'already uploaded' : 'uploaded' });
    } catch (e) { record({ key: 'thumbnail', kind: 'thumbnail', status: 'failed', message: errText(e) }); }
  }

  // per block: decks + datasets only (+ refusals)
  const topics = Array.isArray(index.topics) ? index.topics : [];
  const allBlocks = [];
  topics.forEach((t) => (t.blocks || []).forEach((b) => allBlocks.push(b)));
  let i = 0;
  for (const b of allBlocks) {
    i += 1;
    const key = b.key;
    onProgress({ phase: 'files', current: i, total: allBlocks.length, label: b.title });
    if (b.type === 'Video' && b.file) { record({ key, kind: 'video-file', status: 'refused', message: 'videos must be links, not files' }); continue; }
    try {
      if ((b.type === 'Slides' || b.type === 'LiveClassSlides') && b.file) {
        if (tooBig(b.file)) record({ key, kind: 'deck', status: 'refused', message: `${b.file} is over 50 MB` });
        else {
          const d = await viaLedger(b.file, () => uploadGeneric(b.file, 'content'));
          blockOf(key).deck = d;
          record({ key, kind: 'deck', status: d.cached ? 'skipped' : 'uploaded', id: d.fileId, url: d.url, message: d.cached ? 'already uploaded' : 'uploaded' });
        }
      } else if (b.type === 'GuidedLab' || b.type === 'AILab') {
        for (const ds of (b.datasets || [])) {
          if (!ds?.file) continue;
          if (tooBig(ds.file)) { record({ key, kind: 'dataset', status: 'refused', message: `${ds.file} is over 50 MB` }); continue; }
          try { const d = await viaLedger(ds.file, () => uploadDataset(ds.file)); blockOf(key).datasets.push({ file: ds.file, ...d }); record({ key, kind: 'dataset', status: d.cached ? 'skipped' : 'uploaded', id: d.datasetId, message: d.cached ? 'already uploaded' : 'uploaded' }); }
          catch (e) { record({ key, kind: 'dataset', status: 'failed', message: `${ds.file}: ${errText(e)}` }); }
        }
      }
    } catch (e) { record({ key, kind: b.type, status: 'failed', message: errText(e) }); }
  }

  return { assets, uploads, ledger };
}

export { MAX_FILE_BYTES, ext, errText, ledgerKey, blob, MULTIPART };
